import React, { useState, useMemo, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Grid3X3, LayoutGrid, SlidersHorizontal, X, ChevronDown, 
  ChevronUp, Filter, ArrowUpDown, GitCompare, Check, Star
} from 'lucide-react';
import { Layout } from '@/components/layout';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { hasResponsiveImages, getResponsiveImageProps } from '@/components/shared/ResponsiveProductImage';
import CatalogueFlipbookBanner from '@/components/shared/CatalogueFlipbookBanner';
import { ComparePromoTile } from '@/components/products/ComparePromoTile';
import { CompareBannerMobile } from '@/components/products/CompareBannerMobile';
import { Label } from '@/components/ui/label';
import SEO from '@/components/shared/SEO';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { allProducts, categories, colorTones, patternTypes, budgetLevels, industries } from '@/data/products';
import { cn } from '@/lib/utils';
import SeoContentBlock from '@/components/shared/SeoContentBlock';
import { PRODUCTS_SEO } from '@/content/seoContentCopy';

// Zone Icon Component
const ZoneIcon = ({ zone, size = 28 }) => (
  <img 
    src={`/Assets/icons/zones/zone-${zone}.svg`}
    alt={`Zone ${zone}`}
    className="drop-shadow-md"
    style={{ width: size, height: size }}
  />
);

// Product Card Component with Compare functionality
const ProductCard = ({ product, isCompareSelected, onCompareToggle }) => {
  // "New arrival" star — only shows for products whose `releasedAt` is
  // within the last 30 days. No releasedAt = no badge, so legacy SKUs
  // stay untouched. Date is read at mount; a browser refresh after the
  // 30-day cutoff makes the star disappear on its own.
  const isNewArrival = (() => {
    if (!product.releasedAt) return false;
    const released = new Date(product.releasedAt);
    if (Number.isNaN(released.getTime())) return false;
    const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;
    return Date.now() - released.getTime() < THIRTY_DAYS_MS;
  })();

  return (
    <div className="group relative">
      <Link to={`/products/${product.slug}`}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="card-portico overflow-hidden h-full"
        >
          <div className="relative h-48 overflow-hidden bg-muted">
            {hasResponsiveImages(product.slug) ? (
              <img
                {...getResponsiveImageProps(product.slug, "thumb", product.image, "(max-width: 768px) 50vw, (max-width: 1024px) 33vw, 300px")}
                alt={product.name}
                className={`w-full h-full group-hover:scale-105 transition-transform duration-smooth ${product.image?.endsWith('.svg') ? 'object-contain p-4 bg-white' : 'object-cover'}`}
                loading="lazy"
                decoding="async"
              />
            ) : (
              <img
                src={product.image}
                alt={product.name}
                className={`w-full h-full group-hover:scale-105 transition-transform duration-smooth ${product.image?.endsWith('.svg') ? 'object-contain p-4 bg-white' : 'object-cover'}`}
                loading="lazy"
              />
            )}
          {/* Zone icon - top left */}
          {product.zoneIcon && (
            <div className="absolute top-3 left-3">
              <img 
                src={product.zoneIcon}
                alt={`Zone compatibility`}
                className="h-6 w-auto drop-shadow-md"
              />
            </div>
          )}
          {/* Compare Checkbox - bottom left */}
          <div 
            className="absolute bottom-2 left-2 z-10"
            onClick={(e) => e.preventDefault()}
          >
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onCompareToggle(product);
              }}
              className={cn(
                "w-8 h-8 rounded-lg flex items-center justify-center transition-all duration-200",
                isCompareSelected 
                  ? "bg-primary text-primary-foreground shadow-lg" 
                  : "bg-white/90 hover:bg-white text-muted-foreground hover:text-primary shadow-md"
              )}
              title={isCompareSelected ? "Remove from compare" : "Add to compare"}
            >
              {isCompareSelected ? (
                <Check className="w-4 h-4" />
              ) : (
                <GitCompare className="w-4 h-4" />
              )}
            </button>
          </div>
          {/* New-arrival star - bottom right (auto-expires 30 days after releasedAt) */}
          {isNewArrival && (
            <div
              className="absolute bottom-2 right-2 z-10 flex items-center gap-1 rounded-full bg-primary/95 px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-primary-foreground shadow-md"
              title="New arrival"
              data-testid="new-arrival-badge"
            >
              <Star className="w-3 h-3 fill-current" />
              New
            </div>
          )}
        </div>
        <div className="p-4">
          <p className="text-xs text-muted-foreground mb-1">{product.type}</p>
          <h3 className="font-heading font-semibold text-foreground group-hover:text-primary transition-colors mb-1">
            {product.name}
          </h3>
          <p className="text-sm text-muted-foreground mb-2">{product.variant}</p>
          <p className="text-xs text-muted-foreground">
            {product.colorCount} Colors • {product.sizeFormat}
          </p>
        </div>
      </motion.div>
    </Link>
    </div>
  );
};

// Filter Section Component
const FilterSection = ({ title, children, defaultOpen = true }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <div className="border-b border-border py-4">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between text-left"
      >
        <span className="font-semibold text-sm">{title}</span>
        {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="pt-3 space-y-2">
              {children}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Filter Panel Component
const FilterPanel = ({ 
  filters, 
  setFilters, 
  onClearAll,
  className 
}) => {
  const handleCheckboxChange = (filterType, value) => {
    setFilters(prev => {
      const current = prev[filterType] || [];
      const updated = current.includes(value)
        ? current.filter(v => v !== value)
        : [...current, value];
      return { ...prev, [filterType]: updated };
    });
  };

  return (
    <div className={cn("bg-card rounded-lg border border-border p-4", className)}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-heading font-semibold">Filters</h3>
        <Button variant="ghost" size="sm" onClick={onClearAll}>
          Clear All
        </Button>
      </div>

      {/* Industry Preview */}
      <FilterSection title="Preview by Industry">
        {industries.map((industry) => (
          <div key={industry} className="flex items-center gap-2">
            <Checkbox
              id={`industry-${industry}`}
              checked={filters.industries?.includes(industry)}
              onCheckedChange={() => handleCheckboxChange('industries', industry)}
            />
            <Label htmlFor={`industry-${industry}`} className="text-sm font-normal cursor-pointer">
              {industry}
            </Label>
          </div>
        ))}
      </FilterSection>

      {/* Category */}
      <FilterSection title="Category">
        {categories.map((cat) => (
          <div key={cat.id} className="flex items-center gap-2">
            <Checkbox
              id={`cat-${cat.id}`}
              checked={filters.categories?.includes(cat.id)}
              onCheckedChange={() => handleCheckboxChange('categories', cat.id)}
            />
            <Label htmlFor={`cat-${cat.id}`} className="text-sm font-normal cursor-pointer">
              {cat.name} ({cat.count})
            </Label>
          </div>
        ))}
      </FilterSection>

      {/* Color Tones - Grid Layout with Swatches */}
      <FilterSection title="Color Tones" defaultOpen={false}>
        <div className="grid grid-cols-4 gap-2">
          {colorTones.map((tone) => {
            const isSelected = filters.colorTones?.includes(tone.id);
            return (
              <button
                key={tone.id}
                onClick={() => handleCheckboxChange('colorTones', tone.id)}
                className={cn(
                  "flex flex-col items-center gap-1 p-2 rounded-lg transition-all",
                  isSelected 
                    ? "bg-primary/10 ring-2 ring-primary" 
                    : "hover:bg-muted"
                )}
                title={tone.name}
              >
                <div 
                  className={cn(
                    "w-8 h-8 rounded-full border-2 shadow-sm",
                    isSelected ? "border-primary" : "border-border"
                  )}
                  style={{ 
                    background: tone.gradient || tone.color 
                  }}
                />
                <span className="text-[10px] text-center leading-tight">
                  {tone.name}
                </span>
              </button>
            );
          })}
        </div>
      </FilterSection>

      {/* Pattern Type - Grid Layout with Icons */}
      <FilterSection title="Pattern Type" defaultOpen={false}>
        <div className="grid grid-cols-4 gap-2">
          {patternTypes.map((pattern) => {
            const isSelected = filters.patternTypes?.includes(pattern.id);
            return (
              <button
                key={pattern.id}
                onClick={() => handleCheckboxChange('patternTypes', pattern.id)}
                className={cn(
                  "flex flex-col items-center gap-1 p-2 rounded-lg transition-all",
                  isSelected 
                    ? "bg-primary/10 ring-2 ring-primary" 
                    : "hover:bg-muted"
                )}
                title={pattern.name}
              >
                <div 
                  className={cn(
                    "w-8 h-8 flex items-center justify-center rounded border",
                    isSelected ? "border-primary bg-white" : "border-border bg-muted"
                  )}
                >
                  <img 
                    src={pattern.icon} 
                    alt={pattern.name}
                    className="w-6 h-6 object-contain"
                  />
                </div>
                <span className="text-[10px] text-center leading-tight">
                  {pattern.name}
                </span>
              </button>
            );
          })}
        </div>
      </FilterSection>

      {/* Budget Level */}
      <FilterSection title="Budget Level" defaultOpen={false}>
        {budgetLevels.map((level) => (
          <div key={level.id} className="flex items-center gap-2">
            <Checkbox
              id={`budget-${level.id}`}
              checked={filters.budgetLevels?.includes(level.id)}
              onCheckedChange={() => handleCheckboxChange('budgetLevels', level.id)}
            />
            <Label htmlFor={`budget-${level.id}`} className="text-sm font-normal cursor-pointer">
              {level.label} ({level.range})
            </Label>
          </div>
        ))}
      </FilterSection>

      {/* Zone Compatibility */}
      <FilterSection title="Zone Compatibility" defaultOpen={false}>
        {[
          { id: "1", label: "Zone 1", zones: [1] },
          { id: "2", label: "Zone 2", zones: [2] },
          { id: "3", label: "Zone 3", zones: [3] },
          { id: "1-2", label: "Zone 1–2", zones: [1, 2] },
          { id: "2-3", label: "Zone 2–3", zones: [2, 3] },
          { id: "1-2-3", label: "Zone 1–2–3", zones: [1, 2, 3] },
        ].map((combo) => (
          <div key={combo.id} className="flex items-center gap-2">
            <Checkbox
              id={`zone-${combo.id}`}
              checked={filters.zones?.includes(combo.id)}
              onCheckedChange={() => handleCheckboxChange('zones', combo.id)}
            />
            <Label htmlFor={`zone-${combo.id}`} className="text-sm font-normal cursor-pointer flex items-center gap-2">
              <span className="flex gap-0.5">
                {combo.zones.map(z => (
                  <span key={z} className={cn(
                    "zone-badge w-5 h-5 text-[9px]",
                    z === 1 && "zone-badge-1",
                    z === 2 && "zone-badge-2",
                    z === 3 && "zone-badge-3"
                  )}>
                    Z{z}
                  </span>
                ))}
              </span>
              {combo.label}
            </Label>
          </div>
        ))}
      </FilterSection>
    </div>
  );
};

const ShopFlooringPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'room'
  const [sortBy, setSortBy] = useState('new-arrivals');
  const [displayCount, setDisplayCount] = useState(16);
  const [filters, setFilters] = useState(() => {
    // Initialize filters from URL params
    const parse = (key) => {
      const val = searchParams.get(key);
      return val ? val.split(',').filter(Boolean) : [];
    };
    return {
      categories: parse('category'),
      colorTones: parse('color'),
      patternTypes: parse('pattern'),
      budgetLevels: parse('budget'),
      zones: parse('zones'),
      industries: parse('industry').map(i => decodeURIComponent(i))
    };
  });
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);
  
  // Compare functionality state
  const [selectedForCompare, setSelectedForCompare] = useState([]);
  
  // Handle compare toggle
  const handleCompareToggle = (product) => {
    setSelectedForCompare(prev => {
      const isSelected = prev.some(p => p.id === product.id);
      if (isSelected) {
        return prev.filter(p => p.id !== product.id);
      } else if (prev.length < 4) {
        return [...prev, product];
      }
      return prev; // Max 4 products
    });
  };
  
  // Navigate to compare page
  const goToCompare = () => {
    if (selectedForCompare.length >= 2) {
      const slugs = selectedForCompare.map(p => p.slug).join(',');
      navigate(`/compare?products=${slugs}`);
    }
  };
  
  // Clear compare selection
  const clearCompareSelection = () => {
    setSelectedForCompare([]);
  };

  // Sync filters → URL params (skip on initial mount to avoid double-setting)
  const isInitialMount = React.useRef(true);
  const isExternalNavigation = React.useRef(false);

  // Watch for external navigation changes (e.g., clicking nav links while already on /products)
  useEffect(() => {
    if (isInitialMount.current) return;
    const parse = (key) => {
      const val = searchParams.get(key);
      return val ? val.split(',').filter(Boolean) : [];
    };
    const newFilters = {
      categories: parse('category'),
      colorTones: parse('color'),
      patternTypes: parse('pattern'),
      budgetLevels: parse('budget'),
      zones: parse('zones'),
      industries: parse('industry').map(i => decodeURIComponent(i))
    };
    // Only update if the URL params actually differ from current filters
    const currentParams = new URLSearchParams();
    if (filters.zones.length) currentParams.set('zones', filters.zones.join(','));
    if (filters.categories.length) currentParams.set('category', filters.categories.join(','));
    if (filters.colorTones.length) currentParams.set('color', filters.colorTones.join(','));
    if (filters.patternTypes.length) currentParams.set('pattern', filters.patternTypes.join(','));
    if (filters.budgetLevels.length) currentParams.set('budget', filters.budgetLevels.join(','));
    if (filters.industries.length) currentParams.set('industry', filters.industries.join(','));
    
    if (searchParams.toString() !== currentParams.toString()) {
      isExternalNavigation.current = true;
      setFilters(newFilters);
    }
  }, [searchParams]);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    if (isExternalNavigation.current) {
      isExternalNavigation.current = false;
      return;
    }
    const params = new URLSearchParams();
    if (filters.zones.length) params.set('zones', filters.zones.join(','));
    if (filters.categories.length) params.set('category', filters.categories.join(','));
    if (filters.colorTones.length) params.set('color', filters.colorTones.join(','));
    if (filters.patternTypes.length) params.set('pattern', filters.patternTypes.join(','));
    if (filters.budgetLevels.length) params.set('budget', filters.budgetLevels.join(','));
    if (filters.industries.length) params.set('industry', filters.industries.join(','));
    setSearchParams(params, { replace: true });
  }, [filters, setSearchParams]);

  // Filter products
  const filteredProducts = useMemo(() => {
    return allProducts.filter(product => {
      // Zone filter - combo-based (product must support ALL zones in the selected combo)
      if (filters.zones.length > 0) {
        const zoneComboMap = { "1": [1], "2": [2], "3": [3], "1-2": [1, 2], "2-3": [2, 3], "1-2-3": [1, 2, 3] };
        const matchesAnyCombo = filters.zones.some(comboId => {
          const requiredZones = zoneComboMap[comboId];
          return requiredZones && product.zones && requiredZones.every(z => product.zones.includes(z));
        });
        if (!matchesAnyCombo) return false;
      }

      // Industry filter (OR logic - product matches if it has ANY of the selected industries)
      if (filters.industries.length > 0) {
        if (!product.industries || !product.industries.some(ind => filters.industries.includes(ind))) {
          return false;
        }
      }

      // Color Tones filter (OR logic)
      if (filters.colorTones.length > 0) {
        if (!product.colorTones || !product.colorTones.some(color => filters.colorTones.includes(color))) {
          return false;
        }
      }

      // Pattern Types filter (OR logic)
      if (filters.patternTypes.length > 0) {
        if (!product.patterns || !product.patterns.some(pattern => filters.patternTypes.includes(pattern))) {
          return false;
        }
      }

      // Budget Levels filter (OR logic)
      if (filters.budgetLevels.length > 0) {
        if (!product.budgetLevels || !product.budgetLevels.some(budget => filters.budgetLevels.includes(budget))) {
          return false;
        }
      }

      // Category filter
      if (filters.categories.length > 0) {
        const categoryMap = {
          'broadloom': 'Broadloom Rolls & Sheets',
          'carpet-tile': 'Carpet Tile',
          'custom-logo-mats': 'Custom Logo Mats',
          'luxury-vinyl': 'Luxury Vinyl',
          'grid-entry-systems': 'Entry Systems',
          'commercial-mats': 'Commercial Mats',
          'industrial-anti-fatigue': 'Industrial & Anti-Fatigue',
          'non-slip-flooring': 'Non-Slip Flooring',
          'recycle-rubber': 'Recycle Rubber Flooring',
          'custom-solutions': 'Custom Solutions',
          'accessories': 'Accessories'
        };
        const matchesCategory = filters.categories.some(catId => 
          product.category === categoryMap[catId] || product.categoryId === catId
        );
        if (!matchesCategory) return false;
      }

      return true;
    });
  }, [filters]);

  // Sort products
  const sortedProducts = useMemo(() => {
    const sorted = [...filteredProducts];
    switch (sortBy) {
      case 'name-asc':
        return sorted.sort((a, b) => a.name.localeCompare(b.name));
      case 'name-desc':
        return sorted.sort((a, b) => b.name.localeCompare(a.name));
      case 'new-arrivals':
      default:
        // Newest first: highest id floats to the top, so freshly added SKUs
        // show up at the start of the grid without manual pinning.
        return sorted.sort((a, b) => b.id - a.id);
    }
  }, [filteredProducts, sortBy]);

  // Displayed products (with Load More)
  const displayedProducts = sortedProducts.slice(0, displayCount);

  /**
   * Build a render-only array that injects the Compare-promo placeholder.
   *  - First instance after the 3rd product (index 3)
   *  - Repeats every 16 products on long listings
   *  - Does NOT affect totals, pagination, or analytics impressions
   */
  const displayedItems = useMemo(() => {
    if (displayedProducts.length === 0) return displayedProducts;
    const items = [...displayedProducts];
    const PROMO = { type: 'compare_promo' };
    const FIRST = 3;
    const REPEAT = 16;

    if (items.length >= FIRST) {
      items.splice(FIRST, 0, { ...PROMO, _key: 'promo-0' });
      let next = FIRST + 1 + REPEAT; // +1 because we just inserted one
      let i = 1;
      while (next < items.length) {
        items.splice(next, 0, { ...PROMO, _key: `promo-${i}` });
        next += REPEAT + 1;
        i += 1;
      }
    }
    return items;
  }, [displayedProducts]);

  const handleClearAll = () => {
    setFilters({
      categories: [],
      colorTones: [],
      patternTypes: [],
      budgetLevels: [],
      zones: [],
      industries: []
    });
    setDisplayCount(16);
  };

  const handleLoadMore = () => {
    setDisplayCount(prev => Math.min(prev + 16, sortedProducts.length));
  };

  const activeFilterCount = Object.values(filters).flat().length;

  return (
    <Layout>
      <SEO 
        title="Commercial Flooring Products"
        description="Browse our complete catalog of commercial flooring solutions including carpet tiles, LVT, entry mats, custom logo mats, and grid entry systems. Filter by zone, industry, or style."
        canonicalUrl="/products"
      />
      {/* Hero Section */}
      <section className="bg-primary-bg text-primary-foreground py-12 md:py-16">
        <div className="container-portico">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-2xl"
          >
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
              Products
            </h1>
            <p className="text-lg text-primary-foreground/80">
              Explore our complete catalog of commercial flooring solutions. 
              From broadloom to luxury vinyl, find the perfect floor for your space.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Catalogue Flipbook Banner */}
      <CatalogueFlipbookBanner />

      {/* Main Content */}
      <section className="section-padding-sm bg-background">
        <div className="container-portico">
          <div className="flex gap-8">
            {/* Desktop Filter Sidebar */}
            <aside className="hidden lg:block w-72 flex-shrink-0">
              <FilterPanel 
                filters={filters}
                setFilters={setFilters}
                onClearAll={handleClearAll}
                className="sticky top-32"
              />
            </aside>

            {/* Product Grid */}
            <div className="flex-grow">
              {/* Toolbar */}
              <div className="flex flex-wrap items-center justify-between gap-4 mb-6 pb-4 border-b border-border">
                <div className="flex items-center gap-4">
                  {/* Mobile Filter Button */}
                  <Sheet open={isMobileFilterOpen} onOpenChange={setIsMobileFilterOpen}>
                    <SheetTrigger asChild>
                      <Button variant="outline" className="lg:hidden">
                        <Filter className="w-4 h-4 mr-2" />
                        Filters
                        {activeFilterCount > 0 && (
                          <span className="ml-2 w-5 h-5 bg-primary text-primary-foreground rounded-full text-xs flex items-center justify-center">
                            {activeFilterCount}
                          </span>
                        )}
                      </Button>
                    </SheetTrigger>
                    <SheetContent side="left" className="w-80 p-0 overflow-y-auto">
                      <SheetHeader className="p-4 border-b border-border">
                        <SheetTitle>Filters</SheetTitle>
                      </SheetHeader>
                      <div className="p-4">
                        <FilterPanel 
                          filters={filters}
                          setFilters={setFilters}
                          onClearAll={() => {
                            handleClearAll();
                            setIsMobileFilterOpen(false);
                          }}
                          className="border-0 p-0"
                        />
                      </div>
                      <div className="p-4 border-t border-border">
                        <Button 
                          className="w-full" 
                          onClick={() => setIsMobileFilterOpen(false)}
                        >
                          Apply Filters
                        </Button>
                      </div>
                    </SheetContent>
                  </Sheet>

                  <p className="text-sm text-muted-foreground">
                    <span className="font-semibold text-foreground">{sortedProducts.length}</span> Products
                  </p>
                </div>

                <div className="flex items-center gap-4">
                  {/* View Toggle */}
                  <div className="hidden sm:flex items-center border border-border rounded-md">
                    <button
                      onClick={() => setViewMode('grid')}
                      className={cn(
                        "p-2 transition-colors",
                        viewMode === 'grid' ? 'bg-muted' : 'hover:bg-muted'
                      )}
                      title="Grid View"
                    >
                      <Grid3X3 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setViewMode('room')}
                      className={cn(
                        "p-2 transition-colors",
                        viewMode === 'room' ? 'bg-muted' : 'hover:bg-muted'
                      )}
                      title="Room View"
                    >
                      <LayoutGrid className="w-4 h-4" />
                    </button>
                  </div>

                  {/* Sort */}
                  <Select value={sortBy} onValueChange={setSortBy}>
                    <SelectTrigger className="w-[180px]">
                      <ArrowUpDown className="w-4 h-4 mr-2" />
                      <SelectValue placeholder="Sort by" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="new-arrivals">New Arrivals</SelectItem>
                      <SelectItem value="name-asc">Name (A-Z)</SelectItem>
                      <SelectItem value="name-desc">Name (Z-A)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Active Filters */}
              {activeFilterCount > 0 && (
                <div className="flex flex-wrap items-center gap-2 mb-6">
                  <span className="text-sm text-muted-foreground">Active filters:</span>
                  {filters.zones.map(comboId => {
                    const comboLabels = { "1": "Zone 1", "2": "Zone 2", "3": "Zone 3", "1-2": "Zone 1–2", "2-3": "Zone 2–3", "1-2-3": "Zone 1–2–3" };
                    return (
                    <button
                      key={`zone-${comboId}`}
                      onClick={() => setFilters(prev => ({
                        ...prev,
                        zones: prev.zones.filter(z => z !== comboId)
                      }))}
                      className="inline-flex items-center gap-1 bg-primary/10 text-primary text-sm px-3 py-1 rounded-full hover:bg-primary/20"
                    >
                      {comboLabels[comboId] || comboId}
                      <X className="w-3 h-3" />
                    </button>
                    );
                  })}
                  {filters.categories.map(catId => (
                    <button
                      key={`cat-${catId}`}
                      onClick={() => setFilters(prev => ({
                        ...prev,
                        categories: prev.categories.filter(c => c !== catId)
                      }))}
                      className="inline-flex items-center gap-1 bg-primary/10 text-primary text-sm px-3 py-1 rounded-full hover:bg-primary/20"
                    >
                      {categories.find(c => c.id === catId)?.name}
                      <X className="w-3 h-3" />
                    </button>
                  ))}
                  <button
                    onClick={handleClearAll}
                    className="text-sm text-muted-foreground hover:text-foreground underline"
                  >
                    Clear all
                  </button>
                </div>
              )}

              {/* Product Grid */}
              {displayedProducts.length > 0 ? (
                <>
                  <div className={cn(
                    "grid gap-6",
                    viewMode === 'grid' 
                      ? "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
                      : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3"
                  )}>
                    {displayedItems.map((item) => {
                      if (item.type === 'compare_promo') {
                        return (
                          <React.Fragment key={item._key}>
                            {/* Mobile-only full-width banner */}
                            <div className="sm:hidden col-span-full">
                              <CompareBannerMobile />
                            </div>
                            {/* Desktop / tablet inline tile (same footprint as a ProductCard) */}
                            <div className="hidden sm:block">
                              <ComparePromoTile />
                            </div>
                          </React.Fragment>
                        );
                      }
                      return (
                        <ProductCard
                          key={item.id}
                          product={item}
                          isCompareSelected={selectedForCompare.some(p => p.id === item.id)}
                          onCompareToggle={handleCompareToggle}
                        />
                      );
                    })}
                  </div>

                  {/* Load More */}
                  {displayCount < sortedProducts.length && (
                    <div className="text-center mt-12">
                      <Button 
                        variant="outline" 
                        size="lg"
                        onClick={handleLoadMore}
                      >
                        Load More Products
                      </Button>
                      <p className="text-sm text-muted-foreground mt-2">
                        Showing {displayCount} of {sortedProducts.length} products
                      </p>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-16">
                  <div className="text-muted-foreground mb-4">
                    <SlidersHorizontal className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p className="text-lg font-medium">No products match your selections</p>
                    <p className="text-sm">Try adjusting your filters or clearing them to see more products.</p>
                  </div>
                  <Button onClick={handleClearAll}>
                    Clear All Filters
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
      
      {/* Sticky Compare Bar */}
      <AnimatePresence>
        {selectedForCompare.length > 0 && (
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            className="fixed bottom-0 left-0 right-0 bg-card border-t border-border shadow-lg z-50 py-4"
          >
            <div className="container mx-auto px-4 flex items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="flex -space-x-2">
                  {selectedForCompare.map((product) => (
                    <div 
                      key={product.id}
                      className="w-12 h-12 rounded-lg border-2 border-white overflow-hidden shadow-md"
                    >
                      <img 
                        src={product.image} 
                        alt={product.name}
                        className={`w-full h-full ${product.image?.endsWith('.svg') ? 'object-contain p-1 bg-white' : 'object-cover'}`}
                      />
                    </div>
                  ))}
                </div>
                <div>
                  <p className="font-semibold text-sm">
                    {selectedForCompare.length} product{selectedForCompare.length > 1 ? 's' : ''} selected
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {selectedForCompare.length < 2 ? 'Select at least 2 to compare' : 'Ready to compare'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={clearCompareSelection}
                >
                  Clear
                </Button>
                <Button 
                  size="sm"
                  onClick={goToCompare}
                  disabled={selectedForCompare.length < 2}
                  className="gap-2"
                >
                  <GitCompare className="w-4 h-4" />
                  Compare Now
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      <SeoContentBlock {...PRODUCTS_SEO} />
    </Layout>
  );
};

export default ShopFlooringPage;
